import { CSRF_HEADER, CSRF_VALUE, type ErrorCode, type ProblemDetails } from '@dnx/contracts';

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: ErrorCode, message: string, readonly problem?: ProblemDetails) {
    super(message);
  }
}

const FALLBACK: Partial<Record<ErrorCode, string>> = {
  INTERNAL: 'Não conseguimos concluir agora. Tente de novo em instantes.',
  RATE_LIMITED: 'Muitas tentativas. Aguarde alguns minutos.',
};

/** Cliente único da API: mesma origem, cookie httpOnly, header anti-CSRF, erros RFC 9457. */
export async function api<T>(path: string, init: { method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; body?: unknown } = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method: init.method ?? 'GET',
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        [CSRF_HEADER]: CSRF_VALUE,
        ...(init.body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'INTERNAL', 'Sem conexão com o servidor. Verifique sua internet.');
  }
  if (res.status === 204) return undefined as T;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const p = body as ProblemDetails | null;
    const code = p?.code ?? 'INTERNAL';
    throw new ApiError(res.status, code, p?.detail ?? FALLBACK[code] ?? FALLBACK.INTERNAL!, p ?? undefined);
  }
  return body as T;
}

/** Envio de imagem como corpo binário (fotos clínicas). Mesmo tratamento de erro da `api`. */
export async function uploadImage<T>(path: string, blob: Blob, dims: { width: number; height: number }, query: Record<string, string>): Promise<T> {
  const qs = new URLSearchParams({ ...query, w: String(dims.width), h: String(dims.height) });
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}?${qs}`, {
      method: 'POST', credentials: 'same-origin', body: blob,
      headers: { accept: 'application/json', [CSRF_HEADER]: CSRF_VALUE, 'content-type': blob.type || 'image/jpeg' },
    });
  } catch {
    throw new ApiError(0, 'INTERNAL', 'Sem conexão com o servidor. Verifique sua internet.');
  }
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const p = body as ProblemDetails | null;
    const code = p?.code ?? 'INTERNAL';
    throw new ApiError(res.status, code, p?.detail ?? FALLBACK[code] ?? FALLBACK.INTERNAL!, p ?? undefined);
  }
  return body as T;
}
